import type { ReactNode } from "react";
import {
  Dialog as AriaDialog,
  Button as AriaButton,
  Heading,
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { cn } from "@core/utils";

interface DrawerProps extends Omit<
  ModalOverlayProps,
  "className" | "children"
> {
  /** Panel title — the labelling `<h2>`, never omitted. */
  title: string;
  /** Supporting copy under the title. */
  description?: string;
  children?: ReactNode;
  /** Footer actions, pinned below the scrolling body. */
  actions?: ReactNode;
  /** Inline max width from `sm:` up; full-bleed at 320px regardless. */
  className?: string;
}

/**
 * Side panel primitive on React Aria's `Modal` — focus trap, scroll lock, Escape/overlay
 * dismiss and `aria-labelledby` come from React Aria, so features never hand-roll them.
 *
 * Anchored to the right edge (`justify-end`).
 *
 * Mobile-first: full width at 320px so a list of rows is actually readable, capped from `sm:`.
 * The body scrolls on its own (`min-h-0` + `overflow-y-auto`) so the header and footer stay put
 * however long the list gets.
 */
export function Drawer({
  title,
  description,
  children,
  actions,
  className,
  ...props
}: DrawerProps) {
  const { t } = useTranslation("common");

  return (
    <ModalOverlay
      isDismissable
      className="fixed inset-0 z-50 flex justify-end bg-overlay backdrop-blur-[2px] entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out"
      {...props}
    >
      <Modal
        className={cn(
          "h-full w-full bg-surface-card shadow-lg outline-none sm:max-w-120",
          className
        )}
      >
        <AriaDialog className="flex h-full flex-col outline-none">
          {({ close }) => (
            <>
              <div className="flex shrink-0 items-start gap-xs border-b border-(--border-default) px-md py-md sm:px-lg">
                <div className="flex min-w-0 flex-1 flex-col gap-xxs">
                  <Heading
                    slot="title"
                    className="text-lg-semibold text-(--text-primary)"
                  >
                    {title}
                  </Heading>
                  {description ? (
                    <p className="text-sm-regular text-(--text-secondary)">
                      {description}
                    </p>
                  ) : null}
                </div>
                <AriaButton
                  onPress={close}
                  aria-label={t("actions.dismiss")}
                  className={
                    "-me-2 flex size-11 shrink-0 items-center justify-center rounded-md " +
                    "text-(--text-secondary) outline-none transition-colors " +
                    "hover:bg-gray-50 pressed:bg-gray-100 " +
                    "focus-visible:ring-2 focus-visible:ring-focus-ring"
                  }
                >
                  <Icon name="close" size={20} />
                </AriaButton>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-md py-md sm:px-lg">
                {children}
              </div>

              {actions ? (
                <div className="flex shrink-0 flex-col gap-xs border-t border-(--border-default) px-md py-md *:w-full sm:flex-row sm:items-center sm:justify-end sm:px-lg sm:*:w-auto">
                  {actions}
                </div>
              ) : null}
            </>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

export type { DrawerProps };
